const DEFAULT_TRACKER_URL = 'https://mesimedical.matomo.cloud/matomo.php'
const DEFAULT_SCRIPT_URL = 'https://cdn.matomo.cloud/mesimedical.matomo.cloud/matomo.js'
const DEFAULT_SITE_ID = '1'

export const MATOMO_SCRIPT_ID = 'matomo-tracker'
export const MATOMO_TRACKER_URL = process.env.VUE_APP_MATOMO_URL || DEFAULT_TRACKER_URL
export const MATOMO_SCRIPT_URL = process.env.VUE_APP_MATOMO_SCRIPT_URL || DEFAULT_SCRIPT_URL
export const MATOMO_SITE_ID = process.env.VUE_APP_MATOMO_SITE_ID || DEFAULT_SITE_ID

export const MATOMO_DOMAINS = [
  '*.www.mesimedical.com',
  '*.configurator.mesimedical.com',
  '*.virtual-clinic.mesimedical.com',
  '*.www.mesimtablet.com'
]

let initialized = false

export function getMatomoQueue (targetWindow) {
  const win = targetWindow || window
  win._paq = win._paq || []
  return win._paq
}

export function queueMatomoPageView (queue, pageView) {
  if (pageView.referrerUrl) {
    queue.push(['setReferrerUrl', pageView.referrerUrl])
  }

  queue.push(['setCustomUrl', pageView.url])
  queue.push(['setDocumentTitle', pageView.title])
  queue.push(['trackPageView'])
}

export function queueMatomoInitialization (queue, options) {
  queue.push(['requireCookieConsent'])

  if (options.sharedDomain) {
    queue.push(['setCookieDomain', '.mesimedical.com'])
    queue.push(['setDomains', MATOMO_DOMAINS])
    queue.push(['enableCrossDomainLinking'])
  }

  if (options.secureCookie) {
    queue.push(['setSecureCookie', true])
  }

  if (options.hasCookieConsent) {
    queue.push(['setCookieConsentGiven'])
  }

  queue.push(['setTrackerUrl', options.trackerUrl])
  queue.push(['setSiteId', options.siteId])
  queue.push(['enableLinkTracking'])
  queueMatomoPageView(queue, options)
}

export function setMatomoCookieConsent (granted, queue = getMatomoQueue()) {
  if (granted) {
    queue.push(['setCookieConsentGiven'])
    return
  }

  queue.push(['forgetCookieConsentGiven'])
  queue.push(['deleteCookies'])
}

export function ensureMatomoScript (scriptUrl = MATOMO_SCRIPT_URL, targetDocument = document) {
  const existingScript = targetDocument.getElementById(MATOMO_SCRIPT_ID)
  if (existingScript) {
    return existingScript
  }

  const script = targetDocument.createElement('script')
  script.id = MATOMO_SCRIPT_ID
  script.async = true
  script.src = scriptUrl
  targetDocument.head.appendChild(script)
  return script
}

export function createMatomoRouteTracker (queue, options) {
  let currentUrl = options.initialUrl

  return async (route) => {
    if (options.waitForDom) {
      await options.waitForDom()
    }

    const path = route.fullPath || route.path
    if (!path) {
      return
    }

    const nextUrl = new URL(path, options.origin).href
    if (nextUrl === currentUrl) {
      return
    }

    queueMatomoPageView(queue, {
      referrerUrl: currentUrl,
      title: options.getTitle(),
      url: nextUrl
    })
    currentUrl = nextUrl
  }
}

export function initializeMatomo ({ router, hasCookieConsent, waitForDom }) {
  if (initialized || typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  initialized = true
  const queue = getMatomoQueue()
  const initialUrl = window.location.href
  const isMesimedicalDomain = window.location.hostname === 'mesimedical.com' || window.location.hostname.endsWith('.mesimedical.com')

  queueMatomoInitialization(queue, {
    hasCookieConsent,
    secureCookie: window.location.protocol === 'https:',
    sharedDomain: isMesimedicalDomain,
    siteId: MATOMO_SITE_ID,
    title: document.title,
    trackerUrl: MATOMO_TRACKER_URL,
    url: initialUrl
  })

  const script = ensureMatomoScript()
  script.addEventListener('error', () => {
    // eslint-disable-next-line no-console
    console.error('Failed to load Matomo tracking.')
  }, { once: true })

  if (router) {
    router.afterEach(createMatomoRouteTracker(queue, {
      getTitle: () => document.title,
      initialUrl,
      origin: window.location.origin,
      waitForDom
    }))
  }
}

export function grantMatomoCookieConsent () {
  setMatomoCookieConsent(true)
}

export function revokeMatomoCookieConsent () {
  setMatomoCookieConsent(false)
}
